import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api, { API } from "../lib/api";
import PageMeta from "../components/PageMeta";
import { trackSiteEvent } from "../lib/analytics";
import { Button } from "../components/ui/button";
import { MapPin, CalendarCheck, ArrowRight, Storefront } from "@phosphor-icons/react";

const BASE = "https://thetrucktrackr.com";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "";
const fmtWhen = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "";

function LocalTrucks({ post, upcoming }) {
  const [trucks, setTrucks] = useState(null);
  useEffect(() => {
    api.get("/blog/local-data", { params: { city: post.target_city, state: post.target_state } })
      .then(({ data }) => setTrucks(data.trucks))
      .catch(() => setTrucks([]));
  }, [post.target_city, post.target_state]);
  if (!trucks || trucks.length === 0) return null;
  return (
    <section className="my-8 rounded-2xl border border-border bg-card p-5 sm:p-6 not-prose" data-testid={upcoming ? "blog-upcoming-section" : "blog-local-trucks"}>
      <h2 className="font-display font-bold text-lg md:text-lg">
        {upcoming
          ? `Upcoming food truck locations${post.target_city ? ` around ${post.target_city}` : ""}`
          : `Food trucks near ${post.target_city || post.target_state}`}
      </h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {trucks.map((t) => (
          <Link
            key={t.truck_name}
            to={`/map?truck=${encodeURIComponent(t.event_id)}`}
            onClick={() => trackSiteEvent("blog_truck_click")}
            className="rounded-xl border border-border p-4 hover:border-primary/40 transition-colors block"
            data-testid="blog-local-truck-card"
          >
            <div className="font-semibold text-sm">{t.truck_name}</div>
            <div className="mt-1 text-xs text-muted-foreground">
              {fmtWhen(t.starts_at)}{t.address ? ` · ${t.address}` : ""}
            </div>
          </Link>
        ))}
      </div>
      <p className="mt-3 text-xs text-muted-foreground">Live data from the TruckTrackr map — updated as trucks post.</p>
    </section>
  );
}

const CTA_BLOCKS = {
  cta_map: {
    testid: "blog-cta-map", icon: MapPin, event: "blog_cta_map", to: "/map",
    title: "Find food trucks near you",
    body: "Open the live map and see who's parked, open, and serving right now.",
    btn: "Open the live map",
  },
  cta_request_truck: {
    testid: "blog-cta-request", icon: CalendarCheck, event: "blog_cta_request_truck", to: "/find-a-truck",
    title: "Need a food truck for your event?",
    body: "Tell us about your event and we'll connect you with available local trucks — free for hosts.",
    btn: "Request a food truck",
  },
};

function CtaBlock({ kind }) {
  const c = CTA_BLOCKS[kind];
  if (!c) return null;
  return (
    <section className="my-8 rounded-2xl border border-primary/30 bg-primary/5 p-5 sm:p-6 not-prose" data-testid={c.testid}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1">
          <h2 className="font-display font-bold text-lg md:text-lg flex items-center gap-2">
            <c.icon weight="duotone" className="h-5 w-5 text-primary" /> {c.title}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">{c.body}</p>
        </div>
        <Link to={c.to} onClick={() => trackSiteEvent(c.event)} className="flex-none">
          <Button className="rounded-full gap-2 bg-primary text-primary-foreground hover:bg-primary/90">
            {c.btn} <ArrowRight className="h-4 w-4" />
          </Button>
        </Link>
      </div>
    </section>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | redirect | notfound

  useEffect(() => {
    setState("loading");
    api.get(`/blog/posts/${slug}`)
      .then(({ data }) => {
        if (data.redirect_to) { setPost(data); setState("redirect"); }
        else { setPost(data); setState("ok"); }
      })
      .catch(() => setState("notfound"));
  }, [slug]);

  if (state === "loading") return <div className="mx-auto max-w-3xl px-4 py-20 text-sm text-muted-foreground">Loading…</div>;
  if (state === "redirect") return <Navigate to={`/blog/${post.redirect_to}`} replace />;
  if (state === "notfound") {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center" data-testid="blog-post-not-found">
        <PageMeta title="Article Not Found | TruckTrackr Blog" description="This article doesn't exist or has been removed." />
        <h1 className="font-display font-bold text-3xl">Article not found</h1>
        <p className="mt-3 text-sm text-muted-foreground">This article doesn't exist or has been removed.</p>
        <Link to="/blog" className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
          Browse all articles <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    );
  }

  const coverUrl = post.cover_image_path ? `${API}/files/${post.cover_image_path}` : null;
  const canonical = `${BASE}/blog/${post.slug}`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.meta_description || post.excerpt,
      author: { "@type": "Person", name: post.author },
      publisher: { "@type": "Organization", name: "TruckTrackr", logo: { "@type": "ImageObject", url: `${BASE}/icon-512.png` } },
      datePublished: post.published_at,
      dateModified: post.updated_at,
      mainEntityOfPage: canonical,
      ...(coverUrl ? { image: `${BASE}/api/files/${post.cover_image_path}` } : {}),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: BASE },
        { "@type": "ListItem", position: 2, name: "Blog", item: `${BASE}/blog` },
        { "@type": "ListItem", position: 3, name: post.title, item: canonical },
      ],
    },
  ];
  const sections = post.cta_sections || [];

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-10 sm:py-14" data-testid="blog-post-page">
      <PageMeta
        title={post.seo_title || `${post.title} | TruckTrackr Blog`}
        description={post.meta_description || post.excerpt}
        canonical={canonical}
        ogImage={coverUrl ? `${BASE}/api/files/${post.cover_image_path}` : undefined}
        jsonLd={jsonLd}
      />

      <nav className="text-xs text-muted-foreground" aria-label="Breadcrumb" data-testid="blog-breadcrumb">
        <Link to="/" className="hover:text-foreground">Home</Link> /{" "}
        <Link to="/blog" className="hover:text-foreground">Blog</Link> /{" "}
        <span className="text-foreground">{post.title}</span>
      </nav>

      <div className="mt-4 flex items-center gap-2 text-[11px] uppercase tracking-wider font-bold">
        <Link to={`/blog/category/${post.category_slug}`} className="text-primary hover:underline">{post.category}</Link>
        {post.target_city && (
          <span className="text-muted-foreground font-medium normal-case tracking-normal">
            · {post.target_city}{post.target_state ? `, ${post.target_state}` : ""}
          </span>
        )}
      </div>
      <h1 className="mt-2 font-display font-bold tracking-tighter text-4xl sm:text-5xl leading-[1.08]" data-testid="blog-post-title">
        {post.title}
      </h1>
      <div className="mt-3 text-sm text-muted-foreground">
        By {post.author} · {fmtDate(post.published_at)}
        {post.updated_at && post.updated_at.slice(0, 10) !== (post.published_at || "").slice(0, 10) && (
          <> · Updated {fmtDate(post.updated_at)}</>
        )}
      </div>

      {coverUrl && (
        <img
          src={coverUrl}
          alt={post.cover_image_alt || post.title}
          className="mt-6 w-full max-h-[440px] object-cover rounded-2xl border border-border"
          data-testid="blog-post-cover"
        />
      )}

      <article
        className="mt-8 prose prose-neutral dark:prose-invert max-w-none prose-headings:font-display prose-headings:tracking-tight prose-a:text-primary prose-img:rounded-xl text-[15px] leading-relaxed [&_h2]:text-2xl [&_h3]:text-xl [&_blockquote]:border-l-primary"
        data-testid="blog-post-body"
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={
            /^#\s/m.test(post.content || "")
              // Imported docs often use "#" for major sections — demote one
              // level so the page keeps exactly one H1 (the article title).
              ? { h1: "h2", h2: "h3", h3: "h4", h4: "h5" }
              : undefined
          }
        >
          {post.content}
        </ReactMarkdown>
      </article>

      {sections.includes("trucks_near") && <LocalTrucks post={post} />}
      {sections.includes("upcoming_locations") && !sections.includes("trucks_near") && <LocalTrucks post={post} upcoming />}
      {sections.includes("cta_map") && <CtaBlock kind="cta_map" />}
      {sections.includes("cta_request_truck") && <CtaBlock kind="cta_request_truck" />}

      {post.related?.length > 0 && (
        <section className="mt-12 border-t border-border pt-8" data-testid="blog-related-section">
          <h2 className="font-display font-bold text-lg md:text-lg">Related articles</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            {post.related.map((r) => (
              <Link
                key={r.slug}
                to={`/blog/${r.slug}`}
                onClick={() => trackSiteEvent("blog_related_click")}
                className="rounded-xl border border-border bg-card p-4 hover:border-primary/40 transition-colors"
                data-testid="blog-related-card"
              >
                <div className="text-[10px] uppercase tracking-wider font-bold text-primary">{r.category}</div>
                <div className="mt-1 font-semibold text-sm leading-snug">{r.title}</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="mt-10 rounded-2xl border border-border bg-card p-5 text-center text-sm text-muted-foreground">
        <Storefront weight="duotone" className="inline h-4 w-4 text-primary mr-1" />
        Own a food truck?{" "}
        <Link to="/pricing" className="text-primary font-semibold hover:underline">Get on the map — free</Link>
      </div>
    </div>
  );
}
